import {
  GradientText,
  RightIcon,
  SuccessIcon,
} from "lola-framework-ui-test";
import { useStore } from "../store/globalState";
import {
  resolveStatusToneColor,
  toStatusPresentation,
  type StatusTone,
} from "../utils/operationStatus";
import {
  StatusCircleIcon,
  StatusFailedIcon,
  StatusPendingIcon,
} from "../assets/icons";
import { useTranslation } from "react-i18next";
import { formatAmountWithCode } from "../utils/amountLabel";

export interface TransactionItemProps {
  status: string;
  amount: string;
  currency: string;
  date: string;
  recipientName?: string;
  cashDepositPending?: boolean;
  depositName?: string;
  onDeposit?: () => void;
  launching?: boolean;
  /** Invoked when the user taps a regular (non cash-deposit) transaction row. */
  onSelect?: () => void;
}

/** Initials of first + last name, e.g. "Jane Doe" -> "JD". */
const getInitials = (name?: string): string => {
  const parts = (name || "").split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0][0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? "") : "";
  return `${first}${last}`.toUpperCase();
};

const statusIcon = (tone: StatusTone, color: string) => {
  switch (tone) {
    case "completed":
      return <SuccessIcon size={10} colors={[color, color]} />;
    case "failed":
    case "expired":
      return <StatusFailedIcon size={10} color={color} />;
    case "initial":
      return <StatusPendingIcon size={10} color={color} />;
    case "progress":
    default:
      return <StatusCircleIcon size={10} color={color} />;
  }
};

export const TransactionItem: React.FC<TransactionItemProps> = ({
  status,
  amount,
  currency,
  date,
  recipientName,
  cashDepositPending = false,
  depositName,
  onDeposit,
  launching = false,
  onSelect,
}) => {
  const { t } = useTranslation();
  const { theme } = useStore();
  const colorConfig = theme.whiteView;
  const { bodyCopy, backgroundBtn, textColorBtn, dropzoneColors } = colorConfig;

  // CASH_DEPOSIT operation still awaiting an in-person deposit: a prominent,
  // tappable card inviting the user to complete the deposit.
  if (cashDepositPending) {
    const accent = dropzoneColors?.[0] ?? textColorBtn;
    return (
      <div
        className="button-wrapper transactions-deposit-wrapper"
      >
        <button
          type="button"
          className={`transactions-history-item transactions-deposit-item${
            launching ? " deposit-wave" : ""
          }`}
          style={{
            background: backgroundBtn,
          }}
          onClick={onDeposit}
          disabled={launching}
          aria-busy={launching || undefined}
        >
          <span
            className="transactions-avatar transactions-deposit-avatar"
            style={{ background: textColorBtn, color: backgroundBtn }}
          >
            {getInitials(depositName)}
          </span>
          <div className="transactions-deposit-info">
            <div className="transactions-deposit-copy">
              <GradientText className="transaction-title" textColor={textColorBtn}>
                <b>{depositName}</b>
              </GradientText>
              <GradientText
                className="transactions-deposit-amount"
                textColor={textColorBtn}
                as="span"
              >
                {formatAmountWithCode(amount, currency)}
              </GradientText>
            </div>
            <div className="transactions-deposit-action">
              <GradientText
                className="transactions-deposit-cta"
                textColor={textColorBtn}
              >
                {t("transactions.deposit")}
              </GradientText>
              <span
                className="transactions-deposit-arrow"
                style={{ background: accent }}
              >
                <RightIcon size={32} colors={[textColorBtn, textColorBtn]} />
              </span>
            </div>
          </div>
        </button>
      </div>
    );
  }

  const { tone, labelKey } = toStatusPresentation(status);
  const toneColor = resolveStatusToneColor(tone, backgroundBtn);
  const label = t(labelKey);
  const selectable = typeof onSelect === "function";

  return (
    <section
      className="transactions-history-item"
      onClick={selectable ? onSelect : undefined}
      role={selectable ? "button" : undefined}
      tabIndex={selectable ? 0 : undefined}
      style={selectable ? { cursor: "pointer" } : undefined}
    >
      <p className="transactions-avatar">
        {recipientName ? getInitials(recipientName) : t("transactions.avatar")}
      </p>
      <section className="transactions-info">
        <div className="transactions-row transactions-row--primary">
          <GradientText className="transaction-title" textColor={bodyCopy}>
            <b>{recipientName || t("transactions.transfers")}</b>
          </GradientText>
          <GradientText
            className="transactions-amount"
            textColor={bodyCopy}
            as="span"
          >
            {formatAmountWithCode(amount, currency)}
          </GradientText>
        </div>
        <div className="transactions-row transactions-row--meta">
          <section className="transactions-status">
            {statusIcon(tone, toneColor)}
            <GradientText textColor={toneColor} as="span">
              {label}
            </GradientText>
          </section>
          <GradientText className="transactions-date" textColor={`${bodyCopy}5E`}>
            {date}
          </GradientText>
        </div>
      </section>
    </section>
  );
};
